declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void;
        render: (
          container: HTMLElement,
          params: {
            sitekey: string;
            theme?: "light" | "dark";
            size?: "normal" | "compact";
            tabindex?: number;
            callback?: (token: string) => void;
            "expired-callback"?: () => void;
            "error-callback"?: () => void;
          },
        ) => number;
        reset: (widgetId?: number) => void;
      };
    };
  }
}

// Backend note (not in scope here): token verification must move from the
// classic `siteverify` REST call to Enterprise's
// `projects.assessments.create` — see docs/superpowers/specs for the exact
// request/response shape this pairs with.

// Explicit-render mode doesn't encode a site key in the script URL (the
// widget is rendered into a specific container via grecaptcha.enterprise.render
// once a caller's ref is mounted, not auto-rendered on script load), so one
// script load is shared globally — cached at module scope so StrictMode
// double-mounts or multiple screens never inject it twice.
let scriptPromise: Promise<void> | null = null;

export function loadRecaptchaEnterpriseScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.grecaptcha?.enterprise) {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>("script[data-recaptcha-enterprise]");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load reCAPTCHA Enterprise script")));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/enterprise.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaEnterprise = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA Enterprise script"));
    document.head.appendChild(script);
  });

  // If the script failed to load, let a later mount retry instead of
  // caching the failure forever.
  scriptPromise.catch(() => {
    scriptPromise = null;
  });
  return scriptPromise;
}
